import { store, timer, logPageDuration } from '..';
import { readCookie, removeCookie } from '@common/cookies';
import { config } from '@common/config';
import { ProfileViewModel } from '@models/Auth';

export class CustomError extends Error {
  constructor(message: string) {
    const mess = message || 'Error';
    super(mess); // 'Error' breaks prototype chain here
    Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
  }
}

export class BadRequestError extends CustomError {}
export class NotFoundError extends CustomError {}

export interface ServiceError {
  readonly error: string;
  readonly message?: string;
}

export abstract class BaseService {
  private static isLoggingOut: boolean;
  apiRoot: string;
  private readonly host: string;

  static hasProfile(): boolean {
    const profile = BaseService.getProfileJSON();
    // const token = BaseService.getToken();
    const hasProfileWithRoles = profile && profile.roles ? profile.roles!.length > 0 : false;

    return hasProfileWithRoles;
  }

  static getProfileJSON(): Partial<ProfileViewModel> {
    const profileString = BaseService.getProfile();
    const profile = profileString ? JSON.parse(profileString) : null;

    return profile;
  }

  static getProfile(): string | null {
    return readCookie('profile');
  }

  private static getToken(): string | null {
    let token = readCookie('token');
    if (token) {
      return token;
    }
    return readCookie('access_token');
  }

  constructor(apiRoot: string) {
    var host = config.apiHost;
    this.apiRoot = apiRoot;

    if (host.lastIndexOf('/') === host.length - 1) {
      host = host.substring(0, host.length - 1);
    }

    this.host = host;
  }

  getRootPath(): string {
    return `${this.host}${this.apiRoot ? '/' + this.apiRoot : ''}`;
  }

  fetch<TResponseType>(
    endpoint: string,
    options?: RequestInit,
    isLogout?: boolean,
    isText?: boolean,
    isMultiPart?: boolean
  ): Promise<TResponseType> {
    const init = options || {};
    const headers = init.headers ? new Headers(init.headers) : new Headers();

    if (!isMultiPart) {
      headers.append('Content-Type', 'application/json');
      headers.append('Pragma', 'no-cache');
      headers.append('Expires', Date.now().toString());
      headers.append('Cache-Control', 'no-cache, no-store, must-revalidate');
    }
    if (config.environment === 'Local' && BaseService.hasProfile()) {
      headers.append('Authorization', `Bearer ${BaseService.getToken()}`);
    }

    init.headers = headers;

    const url = isLogout
      ? `${this.host}/${endpoint}`
      : `${this.host}${this.apiRoot ? '/' + this.apiRoot : ''}${
          endpoint.indexOf('?') === 0 || endpoint.length === 0 ? endpoint : '/' + endpoint
        }`;

    return fetch(url, init)
      .then(response => {
        return (isText ? response.text() : response.json())
          .then(json => Promise.resolve({ response, json }))
          .catch(e => Promise.resolve({ response, json: {} }));
      })
      .then(r => {
        if (r.response.status >= 200 && r.response.status < 300) {
          timer.reset(isLogout);
          return r.json as TResponseType;
        } else {
          if (r.response.status === 400) {
            timer.reset(isLogout);
            throw new BadRequestError(
              (r.json as ServiceError).error || (r.json as ServiceError).message || 'General Error'
            );
          } else if (r.response.status === 401 || r.response.status === 403) {
            if (BaseService.hasProfile() && !isLogout) {
              this.logout();
            } else {
              timer.reset(isLogout);
              return {} as TResponseType; // happens if your saved token is bad due to previous logout
            }
          } else if (r.response.status === 404) {
            timer.reset(isLogout);
            throw new NotFoundError(
              (r.json as ServiceError).error || (r.json as ServiceError).message || 'General Error'
            );
          }
          throw new Error(((r.json as ServiceError) || {}).error || ((r.json as ServiceError) || {}).message);
        }
      });
  }

  // This is here instead of AuthService so we can call this any time we get a 401
  async logout() {
    if (BaseService.isLoggingOut || store.getState().auth.isLoggingIn) {
      return Promise.reject('Already Logging out');
    } else {
      BaseService.isLoggingOut = true;
      await store.dispatch({ type: 'LogoutStart' });
    }
    logPageDuration();
    return this.fetch(
      'auth/Logout',
      {
        method: 'POST'
      },
      true
    )
      .then(async r => {
        if (config.environment === 'Local') {
          removeCookie('token');
        }

        removeCookie('profile');
        await store.dispatch({ type: 'LogoutSuccess' });
        // history.push('/login');
        // history.push('/');
        BaseService.isLoggingOut = false;
      })
      .catch(async e => {
        removeCookie('profile');
        removeCookie('access_token');
        if (config.environment === 'Local') {
          removeCookie('token');
        }
        await store.dispatch({ type: 'LogoutFailure' });
        BaseService.isLoggingOut = false;
        return Promise.reject(((e.json as ServiceError) || {}).error || e);
      });
  }
}



// WEBPACK FOOTER //
// ./src/_services/BaseService.ts